/* In-house product options renderer (DPO replacement) — interactivity layer.
 *
 * The markup is server-rendered by snippets/product-options.liquid from
 * custom.option_display; every price shown AND charged comes from
 * custom.option_pricing (embedded here as JSON). This element:
 *   - tracks selections and writes them (as stable keys) into the hidden
 *     properties[_optsel] input the checkout pricing app reads
 *   - evaluates show/hide visibility conditions (hidden = disabled, so
 *     hidden inputs drop out of the submitted form entirely)
 *   - maintains the joined mirror value for checkbox groups
 *   - computes the running total from base price + pricing table
 * Contract: docs/option-schema-v1.md (v1).
 */
(() => {
  if (customElements.get('product-options')) return;

  /* Your looks (§66): one colour, several keys. A thickness is not a
   * colour (Black at 0.6mm), and a white backing is trim construction,
   * not a colour (Blaze (White Backed) on trim palettes is Blaze). The
   * first key in each family is the one a look stores; the others light
   * up wherever a palette offers them instead. Checked against the
   * palette library 2026-09-24; extend here if another family appears —
   * the proper home for this is a family field in the palette data. */
  const YOUR_COLOUR_ALIASES = {
    black: ['black', 'black_heavy'],
    blaze_translucent: ['blaze_translucent', 'blaze_white_backed'],
    acid_translucent: ['acid_translucent', 'acid_white_backed'],
    toxic_translucent: ['toxic_translucent', 'toxic_white_backed'],
    inferno_translucent: ['inferno_translucent', 'inferno_white_backed'],
  };
  const YOUR_COLOUR_CANON = {}; // picked key → the saved key it stands for
  for (const [k, list] of Object.entries(YOUR_COLOUR_ALIASES)) for (const a of list) YOUR_COLOUR_CANON[a] = k;
  const YOUR_SHOWALL_KEY = 'regulation:yourLooks:showAll';

  class ProductOptions extends HTMLElement {
    connectedCallback() {
      this.pricing = this.readPricing();
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0; // cents
      this.optselInput = this.querySelector('[data-optsel]');
      this.mirrorInput = this.querySelector('[data-mirror]');
      this.totalRow = this.querySelector('[data-options-total]');
      this.totalValue = this.querySelector('[data-options-total-value]');
      this.wrappers = Array.from(this.querySelectorAll('[data-opt]'));
      this.groups = Array.from(this.querySelectorAll('[data-opt-group]'));

      // Choice-level conditions (v1.30). Labels are captured BEFORE any
      // choice is killed — the dead state rewrites a native option's text.
      this.choiceConditions = Array.from(this.querySelectorAll('[data-choice-when]'));
      this.buildChoiceLabels();
      // Option keys whose pick a condition cleared, mapped to the option
      // the customer touched to cause it. Held until they answer again.
      this.pendingRepick = new Map();
      this.lastTouchedKey = null;

      // PREVIEW OVERRIDE (2026-09-01, remove once snappy emits section
      // nodes and a product carries one):
      // ?opt_section=<first_key>..<last_key>[:<title>][,<another>]
      // wraps that span of top-level nodes in a section, so the block can
      // be judged before the construct exists upstream. Data cannot stand
      // in for it — option_display is a metafield shared by every theme,
      // so a section authored for a preview would appear on live at the
      // same moment.
      // It wraps whole top-level nodes, never half a group: a section is a
      // span of siblings, and the real thing will be too.
      const sectionParam = new URL(location.href).searchParams.get('opt_section');
      if (sectionParam) {
        const container = this.querySelector('.opt-header')?.parentElement || this;
        const topOf = (key) => {
          let n = this.querySelector(`[data-opt-key="${CSS.escape(key)}"]`);
          while (n && n.parentElement && n.parentElement !== container) n = n.parentElement;
          return n && n.parentElement === container ? n : null;
        };
        for (const spec of sectionParam.split(',')) {
          const m = spec.trim().match(/^([^.]+)\.\.([^:]+)(?::(.*))?$/);
          if (!m) continue;
          const first = topOf(m[1].trim());
          const last = topOf(m[2].trim());
          if (!first || !last) continue;
          const span = [];
          for (let n = first; n; n = n.nextElementSibling) {
            span.push(n);
            if (n === last) break;
          }
          if (span[span.length - 1] !== last) continue; // last came before first
          const box = document.createElement('div');
          box.className = 'opt-section';
          box.dataset.optSection = 'preview';
          first.before(box);
          if (m[3]) {
            const h = document.createElement('h3');
            h.className = 'opt-section__title label';
            const s = document.createElement('span');
            s.className = 'opt-label__title';
            s.textContent = m[3].trim();
            h.append(s);
            box.append(h);
          }
          box.append(...span);
        }
      }

      // PREVIEW OVERRIDE (2026-08-13, remove once a real product carries a
      // conditional choice): ?opt_dead_when=<choice_key>:<option_key>=<choice_key>
      // makes a choice alive ONLY while that option holds that choice, so the
      // dim-and-reset behaviour can be judged before any data exists.
      const whenParam = new URL(location.href).searchParams.get('opt_dead_when');
      for (const spec of (whenParam || '').split(',').map((x) => x.trim()).filter(Boolean)) {
        const m = spec.match(/^([^:]+):([^=]+)=(.+)$/);
        if (!m) continue;
        const vis = { mode: 'all', conditions: [{ option: m[2], is: m[3] }] };
        for (const el of this.querySelectorAll(`[data-key="${CSS.escape(m[1])}"]`)) {
          el.dataset.choiceWhen = JSON.stringify(vis);
          this.choiceConditions.push(el);
        }
      }

      // PREVIEW OVERRIDE (2026-08-12, remove once a real product carries a
      // disabled choice): ?opt_dead=<choice_key>[,<choice_key>] kills the
      // named choices on any product, so the dead state can be judged before
      // the data exists — the render has to be confirmed live BEFORE porting
      // authors a withdrawn service. ?opt_dead_why=<text> supplies the
      // reason line the choice's own `tooltip` would carry.
      const deadParam = new URL(location.href).searchParams.get('opt_dead');
      if (deadParam) {
        const why = new URL(location.href).searchParams.get('opt_dead_why');
        for (const key of deadParam.split(',').map((k) => k.trim()).filter(Boolean)) {
          for (const el of this.querySelectorAll(`[data-key="${CSS.escape(key)}"]`)) {
            this.setChoiceDead(el, why, true);
          }
        }
      }

      // Main price element in this product section — updated live with the
      // configured total (same table the checkout charges from). Target the
      // value span only, preserving the screen-reader label.
      const section = this.closest('section') || document;
      const current = section.querySelector('.product-price .price__current');
      this.priceValueEl = current ? (current.querySelector('.js-value') || current) : null;
      this.configuredValue = null;
      this.configuredMark = null;
      if (current && this.priceValueEl) {
        // Our own value span, sibling to the theme's — the theme's JS can
        // rewrite its span freely; ours only we control.
        this.configuredValue = document.createElement('span');
        this.configuredValue.className = 'price__configured-value';
        this.configuredValue.hidden = true;
        this.priceValueEl.after(this.configuredValue);
        this.configuredMark = document.createElement('span');
        this.configuredMark.className = 'price__configured';
        this.configuredMark.textContent = '* as configured';
        this.configuredMark.hidden = true;
        current.appendChild(this.configuredMark);
      }

      // Held total: the headline price scrolls out of view while the
      // configurator column scrolls, so on the pinned layout the running
      // total row sticks to the foot of the column (CSS) and stays up from
      // page load rather than appearing only once something is priced.
      // Not on a page pinned only to match its sibling: there is nothing to
      // configure there, so a held total would just restate the price.
      // The breakpoint must agree with the CSS block that does the holding
      // (min-width: 769px): product--opt-pinned is emitted by Liquid at every
      // width, so gating on the class alone would force the row visible on
      // mobile, where it is still a plain row that should appear only once
      // something is priced.
      this.pinnedHost = !!this.closest('.product--opt-pinned:not([data-opt-pinned="sibling"])');
      this.heldQuery = window.matchMedia('(min-width: 769px)');
      this.heldTotal = this.pinnedHost && this.heldQuery.matches;
      this.heldQuery.addEventListener('change', () => {
        this.heldTotal = this.pinnedHost && this.heldQuery.matches;
        this.refresh();
      });
      if (this.heldTotal && this.totalValue) {
        this.totalValue.textContent = this.formatMoney(this.basePrice);
      }
      // The theme's sticky ATC panel renders the static base price; it must
      // show what checkout will charge. Looked up lazily on first refresh
      // (null = not yet looked up, false = absent on this page).
      this.stickyAtcPrice = null;
      this.stickyAtcOriginal = '';

      // pick:"one" lanes (offers v1.3): checkbox inputs with radio
      // semantics — ticking an item unticks its siblings. Inputs stay
      // checkboxes so the mirror/composite-key/companion machinery is
      // untouched; this listener runs before the generic refresh below.
      this.addEventListener('change', (e) => {
        const lane = e.target.closest && e.target.closest('[data-pick="one"]');
        if (!lane || !e.target.matches('input[type="checkbox"]') || !e.target.checked) return;
        for (const other of lane.querySelectorAll('input[type="checkbox"]:checked')) {
          if (other !== e.target) other.checked = false;
        }
      });

      // Which option the customer last touched — the choice-level reset
      // notice goes where they are looking, which is here, not on the
      // option that quietly lost its answer.
      this.addEventListener('change', (e) => {
        const w = e.target.closest && e.target.closest('[data-opt]');
        if (w && w.dataset.optKey) this.lastTouchedKey = w.dataset.optKey;
      }, true);

      this.addEventListener('change', () => this.refresh());
      this.addEventListener('input', (e) => {
        if (e.target.matches('input[type="text"]:not([data-combo-search])')) this.refresh();
      });

      // Percent adjustments are defined against the SELECTED variant's
      // unadjusted unit price — re-base when the theme's variant picker
      // fires, so the preview tracks what checkout will charge.
      this.onVariantChange = (e) => {
        const v = e.detail && e.detail.variant;
        if (!v || typeof v.price !== 'number') return;
        const src = e.target instanceof Element ? e.target : null;
        const scope = this.closest('section');
        if (src && scope && !scope.contains(src) && !src.contains(this)) return;
        if (v.price === this.basePrice) return;
        this.basePrice = v.price;
        this.refresh();
      };
      document.addEventListener('on:variant:change', this.onVariantChange);
      this.warnOrphanedConditions();
      this.injectGroupRemovers();
      this.initResetButton();
      this.initCombos();
      this.initSwatchGrids();
      this.initRepeatedPickers();
      this.initYourColours();
      this.initExamplesModals();
      this.initZonedPreviews();
      this.initSizeFitBlocks();
      this.initVariantOffers();
      this.initBuilder();
      this.findTintAnchor();
      this.hydrateFromUrl();
      this.takeOverValidation();
      this.refresh();
      // Transitions arm AFTER first paint: the load-seeded grid must BE
      // open, not perform an entrance.
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('opt-ready')));
    }

    disconnectedCallback() {
      if (this.onVariantChange) document.removeEventListener('on:variant:change', this.onVariantChange);
    }

    /* Any group whose visibility is gated on a single checkbox choice gets a
     * "Remove" link that unticks that gate — so blocks added in error have an
     * obvious way out right where the customer is looking. */
    injectGroupRemovers() {
      for (const g of this.groups) {
        let vis;
        try { vis = JSON.parse(g.dataset.visibility || 'null'); } catch { continue; }
        if (!vis || !vis.conditions || vis.conditions.length !== 1) continue;
        const cond = vis.conditions[0];
        const gateWrap = this.querySelector(`[data-opt-key="${cond.option}"][data-opt-type="checkbox"]`);
        if (!gateWrap) continue;
        const gateInput = gateWrap.querySelector(`input[type="checkbox"][data-key="${cond.is}"]`);
        if (!gateInput) continue;
        /* No heading = nothing to anchor the label to — a floating
         * "Remove this" doesn't say what it removes (user-caught), and the
         * gate checkbox is the same action anyway. Titled groups only. */
        const title = g.querySelector('.opt-group__title');
        if (!title) continue;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'opt-group__remove';
        const idle = 'Remove';
        btn.textContent = idle;
        // Inline confirm: first click arms, second removes; arms auto-reset.
        let timer = null;
        btn.addEventListener('click', () => {
          if (!btn.classList.contains('is-confirming')) {
            btn.classList.add('is-confirming');
            btn.textContent = 'Remove — sure?';
            timer = setTimeout(() => {
              btn.classList.remove('is-confirming');
              btn.textContent = idle;
            }, 4000);
            return;
          }
          clearTimeout(timer);
          btn.classList.remove('is-confirming');
          btn.textContent = idle;
          gateInput.checked = false;
          gateInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
        title.classList.add('opt-group__title--row');
        title.appendChild(btn);
      }
    }

    /* Header "Reset" (same arm-then-confirm as group removers): returns every
     * control to its rendered default and clears the ?cfg= key. Only shown
     * while something is actually configured. */
    initResetButton() {
      this.resetBtn = this.querySelector('[data-config-reset]');
      if (!this.resetBtn) return;
      const btn = this.resetBtn;
      let timer = null;
      /* Reserved-row header (non-scaffold): the title-sized entry arms,
       * the confirm on the right executes. The entry never leaves the
       * flow — pristine state hides it by visibility so the row height
       * (and the configurator's entry point) is constant. */
      const confirm = this.querySelector('[data-config-reset-confirm]');
      this.resetConfirmBtn = confirm || null;
      if (confirm) {
        const disarm = () => {
          clearTimeout(timer);
          confirm.hidden = true;
          btn.classList.remove('is-confirming');
        };
        this.disarmReset = disarm;
        btn.addEventListener('click', () => {
          if (!confirm.hidden) { disarm(); return; }
          confirm.hidden = false;
          btn.classList.add('is-confirming');
          timer = setTimeout(disarm, 4000);
        });
        confirm.addEventListener('click', () => {
          disarm();
          this.resetConfig();
        });
        return;
      }
      btn.addEventListener('click', () => {
        if (!btn.classList.contains('is-confirming')) {
          btn.classList.add('is-confirming');
          btn.textContent = 'Reset — sure?';
          timer = setTimeout(() => {
            btn.classList.remove('is-confirming');
            btn.textContent = 'Reset';
          }, 4000);
          return;
        }
        clearTimeout(timer);
        btn.classList.remove('is-confirming');
        btn.textContent = 'Reset';
        this.resetConfig();
      });
    }

    resetConfig() {
      for (const w of this.wrappers) {
        for (const el of w.querySelectorAll('input, select')) {
          if (el.type === 'radio' || el.type === 'checkbox') el.checked = el.defaultChecked;
          else if (el.type === 'file') el.value = '';
          else if (el.tagName === 'SELECT') {
            const def = Array.from(el.options).findIndex((o) => o.defaultSelected);
            el.selectedIndex = def >= 0 ? def : 0;
          } else if (el.type === 'text') el.value = el.defaultValue;
        }
        this.clearError(w);
      }
      this.refresh();
    }

    /* Variant-choice offer cards (offers v1.1): the size select composes
     * the checkbox's data-key (`item__variant`) and mirror value; ticking
     * without a size auto-picks the first, unticking resets the select. */
    initVariantOffers() {
      for (const sel of this.querySelectorAll('[data-offer-variant-select]')) {
        const card = sel.closest('.opt-upsell__card');
        const box = card && document.getElementById(card.getAttribute('for'));
        if (!box) continue;
        const baseKey = box.dataset.key;
        const template = box.dataset.baseValue || box.value + ' — %v%';
        // Per-variant deal prices (offers v1.3): when option rows carry
        // data-deal-pence, the card's price block follows the SELECTED
        // variant (deal, was-price, save-% eyebrow all recomputed); with
        // nothing selected the card shows the item's own numbers exactly
        // as rendered. Absent data = pre-v1.3 card, nothing moves.
        const priced = !!sel.querySelector('option[data-deal-pence]');
        const dealEl = card.querySelector('.opt-upsell__deal');
        const wasEl = card.querySelector('.opt-upsell__was');
        const eyebrowEl = card.querySelector('.opt-upsell__eyebrow');
        const defaults = priced ? {
          deal: dealEl && dealEl.textContent,
          was: wasEl && wasEl.textContent,
          eyebrow: eyebrowEl && eyebrowEl.textContent,
        } : null;
        const repriceCard = (opt) => {
          if (!priced || !dealEl) return;
          const dp = opt && opt.dataset.dealPence;
          if (dp == null) {
            dealEl.textContent = defaults.deal;
            if (wasEl && defaults.was != null) { wasEl.textContent = defaults.was; wasEl.style.display = ''; }
            if (eyebrowEl && defaults.eyebrow != null) { eyebrowEl.textContent = defaults.eyebrow; eyebrowEl.style.display = ''; }
            return;
          }
          const deal = parseInt(dp, 10) || 0;
          const was = parseInt(opt.dataset.wasPence, 10) || 0;
          const pct = was > 0 ? Math.round(100 - (100 * deal) / was) : 0;
          dealEl.textContent = deal === 0 ? 'Free' : this.formatMoney(deal);
          if (wasEl) {
            wasEl.textContent = this.formatMoney(was);
            wasEl.style.display = deal > 0 && pct === 0 ? 'none' : '';
          }
          if (eyebrowEl) {
            eyebrowEl.textContent = deal === 0 ? 'Free with this item' : 'Save ' + pct + '%';
            eyebrowEl.style.display = deal > 0 && pct === 0 ? 'none' : '';
          }
        };
        const sync = (fromTick) => {
          if (fromTick && box.checked && !sel.value && sel.options.length > 1) sel.selectedIndex = 1;
          if (fromTick && !box.checked) sel.value = '';
          const opt = sel.selectedOptions[0];
          repriceCard(opt && opt.dataset.key ? opt : null);
          if (opt && opt.dataset.key) {
            box.dataset.key = opt.dataset.key;
            box.value = template.replace('%v%', opt.dataset.variantLabel);
            box.checked = true;
          } else {
            box.dataset.key = baseKey;
            box.checked = false;
          }
        };
        sel.addEventListener('change', (e) => {
          e.stopPropagation();
          sync(false);
          box.dispatchEvent(new Event('change', { bubbles: true }));
        });
        box.addEventListener('change', () => sync(true));
      }

      // Pill variant (flat-priced short-label groups): buttons with JS
      // radio semantics — same contract as the select, the checkbox
      // stays the value carrier. Buttons inside the card <label> don't
      // forward activation, so card-toggle and pill-pick never fight.
      for (const grp of this.querySelectorAll('[data-offer-variant-pills]')) {
        const card = grp.closest('.opt-upsell__card');
        const box = card && document.getElementById(card.getAttribute('for'));
        if (!box) continue;
        const baseKey = box.dataset.key;
        const template = box.dataset.baseValue || box.value + ' — %v%';
        const pills = Array.from(grp.querySelectorAll('.opt-upsell__pill'));
        const setActive = (pill) => {
          for (const p of pills) {
            p.classList.toggle('is-active', p === pill);
            p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
          }
          if (pill) {
            box.dataset.key = pill.dataset.key;
            box.value = template.replace('%v%', pill.dataset.variantLabel);
            box.checked = true;
          } else {
            box.dataset.key = baseKey;
            box.checked = false;
          }
        };
        grp.addEventListener('click', (e) => {
          const pill = e.target.closest('.opt-upsell__pill');
          if (!pill || pill.disabled) return;
          e.preventDefault();
          setActive(pill);
          box.dispatchEvent(new Event('change', { bubbles: true }));
        });
        box.addEventListener('change', () => {
          const active = grp.querySelector('.opt-upsell__pill.is-active');
          if (box.checked && !active) {
            const first = pills.find((p) => !p.disabled);
            if (first) setActive(first);
          } else if (!box.checked && active) {
            setActive(null);
          }
        });
      }
    }

    /* Inline categorised swatch grids (contract v1.20, style: "swatches").
     * The label row toggles an in-flow grid; picking collapses back to the
     * row (name + chip shown there — see refresh). Clicks are intercepted
     * like combo panels: the visually-hidden radio never becomes the
     * scroll target (the page-jump bug class). */
    initSwatchGrids() {
      const grids = Array.from(this.querySelectorAll('.opt-swgrid'));
      if (!grids.length) return;
      for (const grid of grids) {
        const toggle = grid.querySelector('[data-swgrid-toggle]');
        const panel = grid.querySelector('[data-swgrid-panel]');
        if (!toggle || !panel) continue;
        // Fully manual: a row toggles ONLY its own grid. No accordion, no
        // auto-fold anywhere (user decision 2026-07-22) — the only
        // automatic act in the whole model is the first unpicked grid
        // opening once at load.
        toggle.addEventListener('click', () => {
          grid.dataset.open = panel.classList.contains('is-open') ? '0' : '1';
          // closing must re-clip IMMEDIATELY (the collapse animation
          // depends on it); settling open releases it on transitionend
          if (grid.dataset.open === '0') panel.classList.remove('opt-swgrid__panel--settled');
          this.refresh();
        });
        panel.addEventListener('transitionend', (e) => {
          if (e.propertyName !== 'grid-template-rows') return;
          panel.classList.toggle('opt-swgrid__panel--settled', panel.classList.contains('is-open'));
        });
        // Picking does NOT collapse — colour choice is the most
        // second-guessed pick on the page; the grid stays put so flipping
        // between candidates is free (the row's name/chip narrate live).
        panel.addEventListener('click', (e) => {
          const label = e.target.closest('label[for]');
          if (!label) return;
          e.preventDefault();
          const input = grid.querySelector(`#${CSS.escape(label.htmlFor)}`);
          if (!input || input.disabled) return;
          input.checked = true;
          input.focus({ preventScroll: true });
          grid.dataset.open = '1'; // picking pins the grid you're in
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      // Deliberately NO generic engagement/blur collapse: folding a grid
      // above the control being engaged reflows the page MID-GESTURE and
      // the tap lands on moved furniture (observed on Dart). Grids move
      // only via their own rows (accordion) — everything else leaves them
      // alone.
    }

    /* Info that would break the visual flow inline (decal artwork examples)
     * opens in a native <dialog> instead; backdrop or ✕ closes. */
    /* Repeated colour pickers (2026-09-02, user-designed).
     *
     * A product that colours several parts from ONE small palette rendered
     * the same picker N times: expand, pick, expand, pick, over a grid
     * that never changes. Four parts by two colours is the same question
     * asked four times.
     *
     * A RUN is two or more adjacent swatch grids that offer exactly the
     * same choice keys. Adjacency is required and boundaries are hard — a
     * run cannot cross out of a section or a group — so this can only ever
     * fire on options the data already places side by side.
     *
     * The chips move onto each part's label row, so a run is answered in
     * one click per part with nothing expanding. Presentation only: the
     * inputs are the product's own radios wherever they end up, so
     * properties, pricing and ?cfg= restore are untouched and the whole
     * treatment can be pulled without touching data. ?opt_repeat=off
     * renders the run exactly as it renders without it, for comparison. */
    initRepeatedPickers() {
      this.repeatRuns = [];
      if (new URL(location.href).searchParams.get('opt_repeat') === 'off') return;

      const sig = (g) => Array.from(g.querySelectorAll('.opt-swgrid__panel input[data-key]'))
        .map((i) => i.dataset.key).join('\u0000');
      const grids = Array.from(this.querySelectorAll('.opt-swgrid'));
      const seen = new Set();
      for (const grid of grids) {
        if (seen.has(grid)) continue;
        const run = [grid];
        const key = sig(grid);
        if (!key) continue;
        for (let n = grid.nextElementSibling; n; n = n.nextElementSibling) {
          if (!n.classList.contains('opt-swgrid') || sig(n) !== key) break;
          run.push(n);
        }
        if (run.length < 2) continue;
        run.forEach((g) => seen.add(g));
        // Only a palette small enough to sit on a row qualifies. A big or
        // categorised one is LEFT ALONE: one grid open at a time was
        // already doing most of the job there, and the alternative — one
        // picker with a list of targets above it — bought two clicks and
        // cost a mode, a palette detached from the label it writes to, and
        // a bracket measured in JS to tie the two back together. Tried,
        // built, and cut on 2026-09-03 as a fake win. It is in the history
        // of feat/repeated-picker if the trade ever looks different.
        const choices = key.split('\u0000').length;
        const grouped = !!grid.querySelector('.opt-swgrid__grouptitle');
        if (choices > 5 || grouped) continue;
        this.buildPickerInline(run);
      }
    }

    /* Chips on the label row, one row per part.
     *
     * The chips are the product's own labels, moved — so a click is an
     * ordinary label activating an ordinary radio, with no handler of ours
     * in the way. The row is built from a picker row's real classes rather
     * than lookalikes: .opt-label-row for the alignment, .opt-swgrid__val
     * for the 20px slot and the inner vertical, .opt-hint inside it for
     * the trim.
     *
     * An answered row names its colour and dims the ones it did not pick.
     * Hiding them was tried first and read as the row losing its options;
     * dimming states the answer just as clearly and keeps every
     * alternative one click away, which matters because colour is the
     * most second-guessed pick on the page — the same reason the big grid
     * refuses to fold on a pick. Dimmed is not dead: these brighten on
     * hover, where a withdrawn choice deliberately never does. */
    buildPickerInline(run) {
      const box = document.createElement('div');
      box.className = 'opt-swinline';
      const rec = { run, box, rows: [] };

      for (const grid of run) {
        const row = document.createElement('div');
        row.className = 'label opt-label-row opt-swinline__row';
        const name = document.createElement('span');
        name.className = 'opt-label__title';
        name.textContent = (grid.querySelector('.opt-label__title')?.textContent || '').trim();
        const val = document.createElement('span');
        val.className = 'opt-swgrid__val';
        const chosen = document.createElement('span');
        chosen.className = 'opt-hint opt-swinline__name';
        const cells = document.createElement('span');
        cells.className = 'opt-swinline__cells';

        for (const input of grid.querySelectorAll('.opt-swgrid__panel input[data-key]')) {
          const label = grid.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          const cell = document.createElement('span');
          cell.className = 'opt-swinline__cell';
          cell.append(input);
          if (label) cell.append(label);
          cells.append(cell);
        }
        val.append(chosen, cells);
        row.append(name, val);

        // Settled, a click anywhere on the row opens it instead of
        // re-picking what is already picked. Open, the click falls through
        // to the label and the change handler settles it again.
        // The inputs have MOVED into this row, so change bubbles here, not
        // to the fieldset they were rendered in.
        row.addEventListener('change', () => this.refresh());

        box.append(row);
        rec.rows.push({ row, chosen, grid });
        grid.classList.add('opt-swgrid--offstage');
      }
      run[0].before(box);
      this.repeatRuns.push(rec);
    }

    /* Your colours (theme-changes §66, brief: regulation-app/docs/my-colours-brief.md).
     *
     * A logged-in customer keeps a small set of palette colours they think
     * of as theirs, chosen on the Your colours page. A CATEGORISED swatch
     * grid (one with subheads) that offers at least one of them shows a
     * read-only "Your colours" group at the top. That is the whole
     * feature on a product page: the group is a sort, not a control.
     *
     * Why categorised only: the group pays for itself where finding a
     * colour is work. Over a five-chip zip row or a black/translucent
     * pair it is furniture (user, 2026-09-19, with screenshots). It is
     * also the exact complement of the inline-row rule, which refuses
     * categorised grids — so every grid gets at most one treatment.
     *
     * Matching is key + material. Leather red is not latex red; the
     * material of a grid is its declared finish (matte = leather, else
     * rubber). A saved colour the grid does not offer is simply not
     * shown — never "not available", whether the reason is material or
     * a restricted palette.
     *
     * The group's chips are SECOND labels for the grid's own radios, so a
     * pick there is the ordinary radio, and pricing, properties and ?cfg=
     * restore never know the group exists. Runs after initRepeatedPickers
     * (that pass must see the grid without this group). */
    initYourColours() {
      if (!this.dataset.customer) return;
      let doc = null;
      try { doc = JSON.parse(this.dataset.yourColours || 'null'); } catch { doc = null; }
      // Named looks (2026-09-24). A bare list from the first cut reads as one look.
      let looks = Array.isArray(doc) ? [{ id: 'look_1', name: 'Your colours', colours: doc }] : (doc && doc.looks);
      if (!Array.isArray(looks)) return;
      this.yourLooks = looks
        .filter((l) => l && l.name && Array.isArray(l.colours))
        .map((l) => ({ id: String(l.id || ''), name: String(l.name), colours: l.colours.filter((c) => c && c.key) }))
        .filter((l) => l.colours.length);
      if (!this.yourLooks.length) {
        // No looks yet: one quiet line above the first categorised
        // palette on the page is the way in. Gone once a look exists,
        // when the block's Edit link takes over.
        const first = Array.from(this.querySelectorAll('.opt-swgrid')).find((g) =>
          !g.classList.contains('opt-swgrid--offstage') && g.querySelector('.opt-swgrid__grouptitle'));
        const inner = first && first.querySelector('.opt-swgrid__panelinner');
        if (!inner) return;
        const note = document.createElement('p');
        note.className = 'opt-swgrid__yoursnote';
        note.append('Did you know? You can save your favourite colour palettes under your account. ');
        const a = document.createElement('a');
        a.href = this.dataset.looksUrl || '/pages/your-colours';
        a.textContent = 'Click here';
        note.append(a);
        inner.prepend(note);
        return;
      }
      this.yourColourGrids = [];
      for (const grid of this.querySelectorAll('.opt-swgrid')) {
        if (grid.classList.contains('opt-swgrid--offstage')) continue;
        if (!grid.querySelector('.opt-swgrid__grouptitle')) continue;
        const inner = grid.querySelector('.opt-swgrid__panelinner');
        if (!inner) continue;
        const box = document.createElement('div');
        box.className = 'opt-swgrid__yours';
        box.hidden = true;
        inner.prepend(box);
        const material = grid.dataset.finish === 'matte' ? 'leather' : 'rubber';
        this.yourColourGrids.push({ grid, box, material });
      }
      // "Show all colours": one preference for the page, kept per device.
      // OFF is the default and is FOCUS: the looks and no palette. Having
      // looks at all is the opt-in; the way out is one tap that sticks.
      // ON shows the looks and the palette.
      this.yourShowAll = false;
      try { this.yourShowAll = localStorage.getItem(YOUR_SHOWALL_KEY) === '1'; } catch { /* storage unavailable */ }
      this.syncYourColours();
    }

    /* Which of the customer's looks the page is drawing from: every look
     * scores the number of picked colours (categorised grids, this
     * material, this grid included) it holds, and the top scorers stand
     * forward; the rest step back. Black + cobalt puts Pup Spark alone in
     * front of the two other looks that only hold black; orange on the
     * body and red on the straps leaves Hi-Viz and Fisty Time level
     * (user, 2026-09-24). Picks in no look score nothing. No score
     * anywhere = every look counts. */
    yourFittingLooks(material) {
      const picked = new Set();
      for (const g of this.yourColourGrids) {
        if (g.material !== material) continue;
        const on = g.grid.querySelector('.opt-swgrid__panel input[data-key]:checked');
        if (on) picked.add(YOUR_COLOUR_CANON[on.dataset.key] || on.dataset.key);
      }
      const score = (l) => l.colours.filter((c) => (c.material || 'rubber') === material && picked.has(c.key)).length;
      const best = Math.max(0, ...this.yourLooks.map(score));
      return best ? this.yourLooks.filter((l) => score(l) === best) : this.yourLooks;
    }

    makeYourToggle() {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'opt-swgrid__yourstoggle';
      btn.addEventListener('click', () => {
        this.yourShowAll = !this.yourShowAll;
        try { localStorage.setItem(YOUR_SHOWALL_KEY, this.yourShowAll ? '1' : '0'); } catch { /* per-device nicety only */ }
        this.refresh();
      });
      return btn;
    }

    /* Rebuild each grid's look rows from the list: one row per look that
     * has at least one colour this grid offers, titled with the look's
     * name. Clones mirror the original label's classes and hidden state
     * every pass, so a dead or condition-hidden colour reads the same in
     * both places. */
    syncYourColours() {
      if (!this.yourColourGrids) return;
      for (const { grid, box, material } of this.yourColourGrids) {
        box.replaceChildren();
        // The block introduces itself and links to where looks are edited.
        const head = document.createElement('div');
        head.className = 'opt-swgrid__yourshead';
        const heading = document.createElement('span');
        heading.className = 'opt-swgrid__yourstitle';
        heading.textContent = 'Your colours';
        const edit = document.createElement('a');
        edit.className = 'opt-swgrid__yoursedit';
        edit.href = this.dataset.looksUrl || '/pages/your-colours';
        edit.textContent = 'Edit';
        head.append(heading, edit);
        box.append(head);
        // Every look always shows. The ones the page is drawing from
        // stay as they are; the rest step back (flat, smaller, name
        // faded). Nothing hides, so nothing has to be escaped and the UI
        // never second-guesses the customer.
        const fitting = this.yourFittingLooks(material);
        // Once anything steps back, the ones left forward speak up.
        box.classList.toggle('opt-swgrid__yours--sorted', fitting.length < this.yourLooks.length);
        let offered = 0;
        for (const look of this.yourLooks) {
          if (look.colours.some((c) => (c.material || 'rubber') === material)) offered++;
          const cells = document.createElement('div');
          cells.className = 'opt-swgrid__grid';
          for (const c of look.colours) {
            if ((c.material || 'rubber') !== material) continue;
            // One chip per saved colour: the exact key, or the first alias
            // the grid offers (the heavy black on a heavy-only palette).
            let input = null;
            for (const key of YOUR_COLOUR_ALIASES[c.key] || [c.key]) {
              input = grid.querySelector(`.opt-swgrid__panel input[data-key="${CSS.escape(key)}"]`);
              if (input) break;
            }
            const orig = input && grid.querySelector(`label.opt-swgrid__chip[for="${CSS.escape(input.id)}"]:not(.opt-swgrid__chip--yours)`);
            if (!orig) {
              // Same material, not on this palette: the saved colour, faded,
              // flat, one plain line through it, not-allowed cursor. Dropping it left the look looking incomplete
              // (user, 2026-09-24). Other-material colours stay silent.
              const gone = document.createElement('span');
              gone.className = 'opt-swgrid__chip opt-swgrid__chip--yours opt-swgrid__chip--absent';
              gone.dataset.name = `${c.label || c.key} — not available on ${this.dataset.garment || 'this garment'}`;
              gone.setAttribute('aria-label', gone.dataset.name);
              const gsw = document.createElement('span');
              gsw.className = 'opt-swatch';
              gsw.style.background = c.swatch || '#888';
              gsw.setAttribute('aria-hidden', 'true');
              gone.append(gsw);
              cells.append(gone);
              continue;
            }
            const clone = document.createElement('label');
            clone.className = orig.className + ' opt-swgrid__chip--yours';
            clone.htmlFor = input.id;
            clone.dataset.name = orig.dataset.name || input.value;
            clone.hidden = orig.hidden;
            const sw = document.createElement('span');
            sw.className = 'opt-swatch';
            sw.setAttribute('style', (orig.querySelector('.opt-swatch') || {}).getAttribute?.('style') || '');
            sw.setAttribute('aria-hidden', 'true');
            clone.append(sw);
            cells.append(clone);
          }
          if (!cells.childElementCount) continue;
          const row = document.createElement('div');
          row.className = 'opt-swgrid__look' + (fitting.includes(look) ? '' : ' opt-swgrid__look--back');
          const name = document.createElement('span');
          name.className = 'opt-swgrid__lookname';
          name.textContent = look.name;
          row.append(cells, name);
          box.append(row);
        }
        // A grid no look touches shows nothing of this and is never
        // focused: there is nothing to focus on. Otherwise the toggle
        // sits at the foot of the panel and focus hides the palette.
        if (offered) {
          const toggle = this.makeYourToggle();
          const label = document.createElement('span');
          label.textContent = this.yourShowAll ? 'Hide full palette' : 'Show all colours';
          const chev = document.createElement('span');
          chev.className = 'opt-swgrid__yourschev';
          chev.setAttribute('aria-hidden', 'true');
          toggle.append(label, chev);
          toggle.classList.toggle('is-open', this.yourShowAll);
          toggle.setAttribute('aria-expanded', String(this.yourShowAll));
          box.append(toggle);
        }
        box.hidden = !offered;
        grid.classList.toggle('opt-swgrid--focused', !!offered && !this.yourShowAll);
      }
    }

    /* The colourway heading (user, 2026-09-24):
     *  - one colour: "Colour: Orange" — "Custom colour" made an ordinary
     *    colour sound special;
     *  - several, and every one of them sits inside one of the customer's
     *    looks: "Your colourway: Hi-Viz" (two looks: "Hi-Viz and Pup
     *    Spark");
     *  - several, no look holds them all: "Custom colourway", exactly as
     *    for a customer with no looks. */
    colourwayTitle(colours) {
      if (colours.length === 1) return { title: `Colour: ${colours[0].name || 'picked'}` };
      const picks = colours.filter((it) => it.key).map((it) => ({
        material: it.material, key: YOUR_COLOUR_CANON[it.key] || it.key,
      }));
      if (picks.length >= 2 && this.yourLooks && this.yourLooks.length) {
        const holds = (look, p) => look.colours.some((c) => (c.material || 'rubber') === p.material && c.key === p.key);
        const names = this.yourLooks.filter((l) => picks.every((p) => holds(l, p))).map((l) => l.name);
        if (names.length) {
          const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
          return { title: `Your colourway: ${list}`, look: list };
        }
      }
      return { title: 'Custom Colourway' };
    }

    /* Runs after the swatch-grid pass in refresh(), so the rows it mirrors
     * have already been written for this tick. */
    syncRepeatedPickers() {
      for (const rec of this.repeatRuns || []) {
        for (const r of rec.rows) {
          const on = r.row.querySelector('.opt-swinline__cell input:checked');
          r.chosen.textContent = on ? on.value : '';
          // A restored or defaulted answer settles too, so a returning
          // customer sees what they picked rather than an open row.
          r.row.classList.toggle('is-settled', !!on);
          // A part whose option is hidden or locked loses its row with it.
          r.row.hidden = r.grid.hidden || r.grid.classList.contains('opt-locked');
        }
        rec.box.hidden = rec.rows.every((r) => r.row.hidden);
      }
    }

    /* Zoned colourway preview, mode "radios" (v1.27, user design): the
     * shape option's plain radio labels each receive a CLONE of the
     * template SVG locked to that choice's shape group — three live
     * previews side by side, all filling from the same pickers. The
     * master SVG stays in the DOM as the clone source but never shows. */
    /* --- Size & fit block: the fit decision is one moment, however many
     * pill scales it takes (size+height, waist+inside leg). Maximal runs
     * of CONSECUTIVE sibling size-role pill options are wrapped in one
     * styled block. Participation is EXPLICIT: only options the library
     * tagged role:"size" (data-opt-role, contract ask pending) join —
     * no key inference, by user ruling 2026-08-08. The M2M escape hatch is
     * auto-detected by canonical choice key (m2m / made_to_measure):
     * deliberately NOT a separate option, so the unit stays a bundleable
     * single and the workshop line still reads "Size: M2M". --- */
    initSizeFitBlocks() {
      const isSize = (el) => el.matches?.('[data-opt].opt-pills') &&
        !el.classList.contains('opt-pills--grid') &&
        el.dataset.optRole === 'size';
      // A run survives interleaved NOTE options (e.g. the hidden M2M
      // measurements note between size and height) — they join the block.
      const isNote = (el) => el.matches?.('[data-opt][data-opt-type="html"]');
      const runs = [];
      for (const wrap of this.querySelectorAll('[data-opt].opt-pills')) {
        if (!isSize(wrap)) continue;
        const last = runs[runs.length - 1];
        if (last) {
          const bridge = [];
          let n = last[last.length - 1].nextElementSibling;
          while (n && isNote(n)) { bridge.push(n); n = n.nextElementSibling; }
          if (n === wrap) { last.push(...bridge, wrap); continue; }
        }
        runs.push([wrap]);
      }
      for (const run of runs) {
        const block = document.createElement('div');
        block.className = 'opt-sizefit';
        run[0].before(block);
        block.append(...run);

        /* Size chart entrance adoption (2026-08-12): the in-house size
         * guide link server-renders in the price block — its no-JS and
         * non-renderer home (snippets/size-chart-link.liquid). The size
         * moment lives HERE, so the first block adopts it into its first
         * scale's legend, the same row the (i) button uses. Move, not
         * copy: one link, and the price block simply no longer has it. */
        if (run === runs[0]) {
          const entrance = this.closest('.shopify-section')
            ?.querySelector('.product-price .size-chart-link');
          const legend = run[0].querySelector('legend.label');
          if (entrance && legend) {
            legend.classList.add('opt-label--has-entrance');
            legend.append(entrance);
          }
        }

        const M2M = /^(m2m|made_to_measure)$/;
        const m2mInput = block.querySelector(
          'input[data-opt-input][data-key="m2m"], input[data-opt-input][data-key="made_to_measure"]');
        const scales = run.filter((w) => isSize(w));
        const sizeKeys = scales.map((w) => w.dataset.optKey);

        /* M2M MODE (user-ratified 2026-08-08). Everything authored "show
         * when the size option's m2m choice is picked" IS the mode's
         * content — any option type (the DPO-era note pattern, recognised
         * and generalised). It moves into the block's panel. */
        const panel = document.createElement('div');
        panel.className = 'opt-sizefit__panel';
        // The theme's baked default content ALWAYS renders — the feature
        // must not depend on the state of legacy data.
        const tpl = this.querySelector('template[data-sizefit-m2m-default]');
        if (tpl) panel.append(tpl.content.cloneNode(true));
        // Recognised m2m-conditioned options still move into the panel;
        // legacy html notes among them are suppressed by CSS class (the
        // default copy replaces them — display, not hidden, so the
        // visibility engine keeps sole ownership of the hidden attr).
        for (const el of this.querySelectorAll('[data-opt][data-visibility]')) {
          let vis;
          try { vis = JSON.parse(el.dataset.visibility || 'null'); } catch { continue; }
          const c = vis && vis.conditions && vis.conditions.length === 1 && vis.conditions[0];
          if (c && sizeKeys.includes(c.option) && M2M.test(c.is)) {
            if (el.dataset.optType === 'html') el.classList.add('opt-sizefit__legacy');
            panel.append(el);
          }
        }
        if (!m2mInput) { block.append(panel); continue; }
        const escape = block.querySelector(`label[for="${m2mInput.id}"]`) || m2mInput.nextElementSibling;
        if (!escape) { block.append(panel); continue; }

        /* The escape belongs to the BLOCK, not the scale that carries it —
         * the label moves to the foot (input stays put; label[for] spans
         * the document). In mode it is the solid banner. */
        escape.classList.add('opt-sizefit__escape');
        const box = document.createElement('span');
        box.className = 'opt-check__tick';
        box.setAttribute('aria-hidden', 'true');
        escape.append(box);
        const or_ = document.createElement('div');
        or_.className = 'opt-sizefit__or';
        or_.append(Object.assign(document.createElement('span'), { textContent: 'OR' }));
        block.append(or_, escape, panel);

        /* Superseded scales stamp "Made to Measure" on the order via
         * hidden mirrors — display property only; _optsel and pricing ride
         * the real m2m choice, which stays checked. */
        const ownScale = m2mInput.closest('[data-opt]');
        const others = scales.filter((w) => w !== ownScale).map((wrap) => {
          const radio = wrap.querySelector('input[data-opt-input]');
          const mirror = document.createElement('input');
          mirror.type = 'hidden';
          mirror.name = radio ? radio.name : '';
          mirror.value = 'Made to Measure';
          mirror.disabled = true;
          const formId = radio && radio.getAttribute('form');
          if (formId) mirror.setAttribute('form', formId);
          panel.before(mirror); // property order: scales, mirrors, then panel content
          return { wrap, mirror };
        });

        let prevOwn = null;
        let active = null;
        const sync = () => {
          const on = !!m2mInput.checked;
          if (on === active) return;
          active = on;
          block.classList.toggle('opt-sizefit--m2m', on);
          /* Sibling scales are marked superseded — the visibility ENGINE
           * hides them, disables their inputs (checked picks survive
           * disabled and revive on untick), suspends required and drops
           * them from price/properties. One owner, no fighting. The own
           * scale hides by CSS only: its m2m choice IS the selection. */
          for (const { wrap, mirror } of others) {
            if (on) wrap.dataset.superseded = '1';
            else delete wrap.dataset.superseded;
            mirror.disabled = !on;
          }
          // measurement-route radios ride the order only in M2M mode
          for (const r of panel.querySelectorAll('input[data-sizefit-route]')) r.disabled = !on;
        };
        (this.sizeFitSyncs || (this.sizeFitSyncs = [])).push(sync);
        block.addEventListener('change', (e) => {
          const t = e.target;
          if (t.matches && t.matches('input[data-opt-input]') && ownScale.contains(t) &&
              t !== m2mInput && t.checked) prevOwn = t;
          sync();
        });
        /* Checkbox behaviour on a radio choice: clicking the active banner
         * unticks it and the held previous pick returns. */
        const untick = () => {
          if (!m2mInput.checked) return;
          m2mInput.checked = false;
          if (prevOwn) prevOwn.checked = true;
          m2mInput.dispatchEvent(new Event('change', { bubbles: true }));
        };
        escape.addEventListener('click', (e) => {
          if (!m2mInput.checked) return;
          e.preventDefault();
          untick();
        });
        /* Secondary escape at the panel foot: after reading the M2M copy
         * the banner is off-screen above, so the way back is restated
         * where the reader ends up. */
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'opt-sizefit__back';
        back.textContent = '‹ Back to standard sizes';
        back.addEventListener('click', untick);
        panel.append(back);
        sync();
      }
    }

    initZonedPreviews() {
      for (const zp of this.querySelectorAll('[data-zoned-preview][data-zoned-mode="radios"]')) {
        const shapeW = zp.dataset.shapeOption
          ? this.querySelector(`[data-opt-key="${zp.dataset.shapeOption}"]`)
          : null;
        const master = zp.querySelector('svg');
        if (!shapeW || !master) continue;
        for (const input of shapeW.querySelectorAll('input[data-opt-input]')) {
          const label = shapeW.querySelector(`label[for="${input.id}"]`);
          if (!label) continue;
          const clone = master.cloneNode(true);
          clone.setAttribute('data-zoned-clone', '');
          clone.setAttribute('data-locked-shape', input.dataset.key || '');
          const pic = document.createElement('span');
          pic.className = 'opt-zoned-card__pic';
          pic.setAttribute('aria-hidden', 'true');
          pic.appendChild(clone);
          label.classList.add('opt-zoned-card');
          // NOT a theme button: .opt-label/.btn paint state via absolutely
          // positioned pill overlays; unanchored they stretch over the
          // whole info column and eat clicks (user-caught). Same rule as
          // image cards: plain element, own two-line state CSS.
          // opt-imgcard included: an image_grid shape option (side_stripe)
          // must shed its card chrome too, or it fights the zoned-card CSS
          label.classList.remove('opt-label', 'opt-label--btn', 'btn', 'relative', 'text-center', 'opt-imgcard');
          label.prepend(pic);
        }
        shapeW.classList.add('opt-zoned-cards');
        shapeW.dataset.zonedShape = zp.dataset.shapeOption; // lets CSS size per template

        zp.hidden = true; // master = clone source only in radios mode
        this.zonedClonesHost = shapeW;
      }
    }

    initExamplesModals() {
      for (const btn of this.querySelectorAll('[data-examples-open], [data-sensitivity-open], [data-info-open], [data-quickview-open]')) {
        // (i) buttons (contract v1.24) live inside their option's label row;
        // the dialog is the wrapper's last child (never inside the <label>,
        // whose clicks forward to the control). Quick-view (i)s sit inside
        // an offer CARD label, so their dialog is id-paired instead.
        const dialog = btn.hasAttribute('data-info-open')
          ? btn.closest('[data-opt]')?.querySelector('dialog[data-info-dialog]')
          : btn.hasAttribute('data-quickview-open')
            ? this.querySelector(`dialog[data-quickview="${btn.dataset.quickviewOpen}"]`)
            : btn.parentElement.querySelector('dialog');
        if (!dialog) continue;
        btn.addEventListener('click', (e) => {
          e.preventDefault(); // cancel <label> click-forwarding to the input
          e.stopPropagation();
          dialog.showModal();
        });
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog || e.target.closest('[data-examples-close]')) dialog.close();
        });
      }
    }

    /* The FIRST colour picker — main colour by convention — is what the
     * size pictograms tint their garment from. This used to be a by-product
     * of injecting the swatch-book link (the link is now rendered in Liquid
     * from the page's actual palettes, see product-options.liquid), so the
     * anchor is found on its own rather than left as a leftover of a job
     * that no longer happens here. */
    findTintAnchor() {
      this.tintAnchor = Array.from(this.querySelectorAll('.opt-combo, .opt-swgrid'))
        .find((c) => c.querySelector('.opt-combo__chip, .opt-swatch') && !c.closest('[data-opt-group]')) || null; // skip decal-block combos
    }

    /* Amalgamated discolouration notice: worst sensitivity grade across all
     * ACTIVE selected graded choices — one line, shown even for "low" (the
     * grade doubles as reassurance), silent when nothing graded is picked.
     * The hidden input writes the shown notice onto the order (the
     * "you were warned" record). */
    updateSensitivityNote() {
      const note = this.querySelector('[data-sensitivity-note]');
      if (!note) return;
      // Products with graded choices reserve the note's space from first
      // paint (invisible, not display:none) — picking a colour must not
      // shift the order button below it.
      if (this.hasGradedChoices === undefined) {
        this.hasGradedChoices = !!this.querySelector('[data-sensitivity]');
      }
      let max = -1;
      for (const w of this.wrappers) {
        if (w.hidden || w.classList.contains('opt-locked')) continue;
        const chosen = [];
        if (w.dataset.optType === 'drop_down') {
          const sel = w.querySelector('select');
          if (sel && sel.selectedOptions[0]) chosen.push(sel.selectedOptions[0]);
        } else {
          chosen.push(...w.querySelectorAll('input:checked'));
        }
        for (const el of chosen) {
          if (el.dataset.sensitivity !== undefined) {
            max = Math.max(max, parseInt(el.dataset.sensitivity, 10) || 0);
          }
        }
      }
      let tiers = ['low', 'medium', 'high'];
      try { tiers = JSON.parse(note.dataset.tiers) || tiers; } catch { /* fallback */ }
      if (max < 0) {
        if (this.hasGradedChoices) {
          note.hidden = false;
          note.classList.add('opt-sensitivity--reserved');
          // longest tier word as the sizing stand-in, so revealing never rewraps
          note.querySelector('[data-sensitivity-tier]').textContent =
            tiers.reduce((a, b) => (b.length > a.length ? b : a), '');
        } else {
          note.hidden = true;
        }
        return;
      }
      const tier = tiers[Math.min(max, tiers.length - 1)];
      note.hidden = false;
      note.classList.remove('opt-sensitivity--reserved');
      note.querySelector('[data-sensitivity-tier]').textContent = tier;
    }

    /* Diagnostics only: an orphaned visibility condition (referencing a
     * nonexistent option or choice key) is permanently false and its
     * dependents silently never render. Compile-time validation is snappy's
     * job; this warning makes a bad compile visible in preview. */
    warnOrphanedConditions() {
      const known = {};
      for (const w of this.wrappers) {
        known[w.dataset.optKey] = new Set(
          Array.from(w.querySelectorAll('[data-key]')).map((el) => el.dataset.key)
        );
      }
      const check = (el, owner) => {
        let vis;
        try { vis = JSON.parse(el.dataset.visibility || 'null'); } catch { return; }
        for (const c of (vis && vis.conditions) || []) {
          if (!(c.option in known)) {
            console.warn(`[product-options] "${owner}" condition references unknown option "${c.option}" — permanently hidden`);
          } else if (!known[c.option].has(c.is)) {
            console.warn(`[product-options] "${owner}" condition references unknown choice "${c.option}:${c.is}" — permanently hidden`);
          }
        }
      };
      for (const w of this.wrappers) if (w.dataset.visibility) check(w, w.dataset.optKey);
      for (const g of this.groups) if (g.dataset.visibility) check(g, g.dataset.optGroup);
    }

    /* --- Searchable combo panels (media drop_downs): DPO-style closed
     * select that opens a filterable list of swatch/image rows. The state
     * carriers are ordinary hidden radios, so pricing/validation/persistence
     * are untouched. --- */

    initCombos() {
      this.combos = Array.from(this.querySelectorAll('.opt-combo'));
      for (const combo of this.combos) {
        const trigger = combo.querySelector('[data-combo-trigger]');
        const panel = combo.querySelector('[data-combo-panel]');
        const search = combo.querySelector('[data-combo-search]');

        // The rows' hidden radios are absolutely positioned against the
        // combo, so they DON'T move when the list scrolls — a native label
        // click then focuses a radio stranded below the fold and the page
        // jumps to it. Intercept: select manually, focus without scrolling.
        panel.addEventListener('click', (e) => {
          const row = e.target.closest('[data-combo-row]');
          if (!row) return;
          e.preventDefault();
          const input = document.getElementById(row.getAttribute('for'));
          if (!input || input.disabled) return;
          input.focus({ preventScroll: true });
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        trigger.addEventListener('click', () => {
          const open = panel.hidden;
          this.closeCombos();
          if (open) {
            panel.hidden = false;
            trigger.setAttribute('aria-expanded', 'true');
            if (search) { search.value = ''; this.filterCombo(combo, ''); search.focus(); }
          }
        });
        if (search) {
          search.addEventListener('input', () => this.filterCombo(combo, search.value));
        }
        combo.addEventListener('change', () => {
          panel.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        });
        combo.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') { panel.hidden = true; trigger.setAttribute('aria-expanded', 'false'); trigger.focus(); }
        });
      }
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.opt-combo')) this.closeCombos();
      });
    }

    closeCombos() {
      for (const combo of this.combos || []) {
        combo.querySelector('[data-combo-panel]').hidden = true;
        combo.querySelector('[data-combo-trigger]').setAttribute('aria-expanded', 'false');
      }
    }

    filterCombo(combo, term) {
      const t = term.trim().toLowerCase();
      for (const row of combo.querySelectorAll('[data-combo-row]')) {
        row.hidden = t !== '' && !row.dataset.searchLabel.includes(t);
      }
    }

    updateComboTriggers() {
      for (const combo of this.combos || []) {
        const valueEl = combo.querySelector('[data-combo-value]');
        const on = combo.querySelector('input[type="radio"]:checked');
        if (!on) { valueEl.textContent = valueEl.dataset.placeholder || '-- Please Select --'; continue; }
        const row = combo.querySelector(`[data-combo-row][for="${on.id}"]`) ||
                    on.nextElementSibling;
        valueEl.innerHTML = '';
        const media = row.querySelector('.opt-combo__chip, .opt-combo__thumb');
        if (media) valueEl.appendChild(media.cloneNode(true));
        valueEl.appendChild(document.createTextNode(' ' + row.querySelector('.opt-combo__label').textContent.trim()));
      }
    }

    /* --- Inline validation: replaces the browser's default bubbles with
     * styled messages under each missing option. Native validation is
     * disabled on this form only; invalid submits are blocked before the
     * theme's AJAX handler runs. --- */

    takeOverValidation() {
      const form = document.getElementById(this.dataset.formId);
      if (!form) return;
      form.noValidate = true;
      form.addEventListener('submit', (e) => {
        const problems = this.validateAll();
        if (problems.length === 0) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        problems[0].wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, true);
    }

    validateAll() {
      const problems = [];
      for (const w of this.wrappers) {
        this.clearError(w);
        if (w.hidden || w.classList.contains('opt-locked') || w.dataset.required !== 'true') continue;
        const type = w.dataset.optType;
        const title = this.optionTitle(w);
        let message = null;
        if (type === 'drop_down') {
          const chosen = w.querySelector('select')?.selectedOptions[0];
          if (!chosen || !chosen.dataset.key) message = `Please choose ${title.toLowerCase().startsWith('decal') ? '' : 'your '}${title}`;
        } else if (type === 'radio') {
          if (!w.querySelector('input[type="radio"]:checked')) message = `Please choose ${title}`;
        } else if (type === 'field') {
          const input = w.querySelector('input[type="text"]');
          if (input && !input.value.trim()) message = `Please fill in ${title}`;
        } else if (type === 'file') {
          const input = w.querySelector('input[type="file"]');
          if (input && (!input.files || !input.files.length)) message = `Please attach ${title}`;
        } else if (type === 'upsell') {
          // Required lanes (offers v1.3 stage/pick semantics)
          if (!w.querySelector('input[type="checkbox"]:checked')) message = `Please choose ${title}`;
        }
        if (message) {
          this.showError(w, message);
          problems.push({ wrapper: w, message });
        }
      }
      return problems;
    }

    /* True if the wrapper currently fails its requirement (no side effects). */
    validateOne(w) {
      if (w.hidden || w.classList.contains('opt-locked') || w.dataset.required !== 'true') return false;
      const type = w.dataset.optType;
      if (type === 'drop_down') {
        const chosen = w.querySelector('select')?.selectedOptions[0];
        return !chosen || !chosen.dataset.key;
      }
      if (type === 'radio') return !w.querySelector('input[type="radio"]:checked');
      if (type === 'field') {
        const input = w.querySelector('input[type="text"]');
        return !!input && !input.value.trim();
      }
      if (type === 'file') {
        const input = w.querySelector('input[type="file"]');
        return !!input && (!input.files || !input.files.length);
      }
      if (type === 'upsell') return !w.querySelector('input[type="checkbox"]:checked');
      return false;
    }

    showError(w, message) {
      w.classList.add('opt-invalid');
      let el = w.querySelector('.opt-error');
      if (!el) {
        el = document.createElement('p');
        el.className = 'opt-error';
        w.appendChild(el);
      }
      el.textContent = message;
    }

    clearError(w) {
      w.classList.remove('opt-invalid');
      w.querySelector('.opt-error')?.remove();
    }

    /* --- URL persistence: the in-progress config lives in ?cfg= (base64url
     * JSON) via replaceState, so reloads keep the customer's work and links
     * are shareable. Files cannot be restored (browser security) — restored
     * configs flag those inputs for re-attachment. --- */

    serializeConfig() {
      const cfg = { s: {}, t: {}, f: [] };
      for (const w of this.wrappers) {
        const key = w.dataset.optKey;
        const type = w.dataset.optType;
        if (type === 'drop_down') {
          // defaultSelected options get the same skip as defaultChecked
          // radios below: rendered defaults aren't customer configuration.
          const chosen = w.querySelector('select')?.selectedOptions[0];
          if (chosen && chosen.dataset.key && !chosen.defaultSelected) cfg.s[key] = chosen.dataset.key;
        } else if (type === 'radio') {
          // defaultChecked radios (e.g. the lettering source) are the
          // rendered default — not customer configuration; skipping them
          // keeps fresh pages (and reset) at a clean URL.
          const on = w.querySelector('input[type="radio"]:checked');
          if (on && !on.defaultChecked) cfg.s[key] = on.dataset.key;
        } else if (type === 'checkbox' || type === 'upsell') {
          const on = Array.from(w.querySelectorAll('input[type="checkbox"]:checked')).filter((i) => !i.defaultChecked);
          if (on.length) cfg.s[key] = on.map((i) => i.dataset.key).join(',');
        } else if (type === 'field') {
          const v = w.querySelector('input[type="text"]')?.value;
          if (v && v.trim()) cfg.t[key] = v;
        } else if (type === 'file') {
          const input = w.querySelector('input[type="file"]');
          if (input && input.files && input.files.length) cfg.f.push(key);
        }
      }
      return cfg;
    }

    updateUrl(cfg) {
      const url = new URL(location.href);
      const empty = !Object.keys(cfg.s).length && !Object.keys(cfg.t).length && !cfg.f.length;
      if (empty) {
        url.searchParams.delete('cfg');
      } else {
        const enc = btoa(unescape(encodeURIComponent(JSON.stringify(cfg))))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        url.searchParams.set('cfg', enc);
      }
      history.replaceState(history.state, '', url);
    }

    hydrateFromUrl() {
      const enc = new URL(location.href).searchParams.get('cfg');
      if (!enc) return;
      let cfg;
      try {
        cfg = JSON.parse(decodeURIComponent(escape(
          atob(enc.replace(/-/g, '+').replace(/_/g, '/')))));
      } catch { return; }
      for (const [key, val] of Object.entries(cfg.s || {})) {
        const w = this.querySelector(`[data-opt-key="${key}"]`);
        if (!w) continue;
        const type = w.dataset.optType;
        // Dead choices are skipped on restore: a shared ?cfg= link can carry
        // a key that has since been withdrawn, and assigning it in script
        // bypasses the disabled attribute the browser enforces on clicks.
        if (type === 'drop_down') {
          const sel = w.querySelector('select');
          const opt = sel && [...sel.options].find((o) => o.dataset.key === val);
          if (opt && !opt.disabled) sel.value = opt.value;
        } else if (type === 'radio') {
          const r = w.querySelector(`input[type="radio"][data-key="${val}"]:not([data-dead])`);
          if (r) r.checked = true;
        } else {
          for (const k of String(val).split(',')) {
            const c = w.querySelector(`input[type="checkbox"][data-key="${k}"]:not([data-dead])`);
            if (c) { c.checked = true; continue; }
            // Variant-choice offer selections restore via the size select
            // (the checkbox's data-key is composed at pick time).
            const opt = w.querySelector(`[data-offer-variant-select] option[data-key="${k}"]`);
            if (opt) {
              const sel = opt.closest('select');
              sel.value = opt.value;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
            // ...or via a size pill (flat-priced short-label groups)
            const pill = w.querySelector(`[data-offer-variant-pills] .opt-upsell__pill[data-key="${k}"]`);
            if (pill && !pill.disabled) pill.click();
          }
        }
      }
      for (const [key, val] of Object.entries(cfg.t || {})) {
        const input = this.querySelector(`[data-opt-key="${key}"] input[type="text"]`);
        if (input) input.value = val;
      }
      for (const key of cfg.f || []) {
        const w = this.querySelector(`[data-opt-key="${key}"]`);
        if (w && !w.querySelector('.opt-file__reattach')) {
          const note = document.createElement('p');
          note.className = 'opt-file__reattach';
          note.textContent = 'Please re-attach your artwork file.';
          w.appendChild(note);
        }
      }
    }

    readPricing() {
      let table = {};
      try {
        table = JSON.parse(this.querySelector('[data-option-pricing]').textContent).options || {};
      } catch { /* no options pricing (offers-only product) */ }
      // Offers (custom.option_offers) carry deal prices inline; merge them
      // into the same lookup shape the total calculation already uses.
      try {
        const doc = JSON.parse(this.querySelector('[data-option-offers]').textContent);
        for (const offer of (doc && doc.offers) || []) {
          table[offer.key] = table[offer.key] || {};
          for (const item of offer.items || []) {
            if (Array.isArray(item.variants)) {
              // Variant-choice item (offers v1.1) — composite keys, same as
              // the checkout function's merge.
              for (const v of item.variants) {
                table[offer.key][item.key + '__' + v.key] = { companion: { variant_id: v.variant_id, price: v.price != null ? v.price : item.price } };
              }
              continue;
            }
            table[offer.key][item.key] = { companion: { variant_id: item.variant_id, price: item.price } };
          }
        }
      } catch { /* no offers */ }
      return table;
    }

    /* Current selection per option key: string key, array of keys, or
     * {len} for priced text fields. Only visible controls count. */
    getState() {
      const state = {};
      for (const w of this.wrappers) {
        if (w.hidden || w.classList.contains('opt-locked')) continue;
        const key = w.dataset.optKey;
        const type = w.dataset.optType;
        if (type === 'html') continue;
        if (type === 'drop_down') {
          const sel = w.querySelector('select');
          const chosen = sel.selectedOptions[0];
          if (chosen && chosen.dataset.key) state[key] = chosen.dataset.key;
        } else if (type === 'radio') {
          const on = w.querySelector('input[type="radio"]:checked');
          if (on) state[key] = on.dataset.key;
        } else if (type === 'checkbox') {
          const on = Array.from(w.querySelectorAll('input[type="checkbox"]:checked'));
          if (on.length) state[key] = on.map((i) => i.dataset.key);
        } else if (type === 'field') {
          const input = w.querySelector('input[type="text"]');
          if (input && input.value.trim()) state[key] = { len: input.value.trim().length };
        } else if (type === 'upsell') {
          const on = Array.from(w.querySelectorAll('input[type="checkbox"]:checked'));
          if (on.length) state[key] = on.map((i) => i.dataset.key);
        } else if (type === 'file') {
          const input = w.querySelector('input[type="file"]');
          if (input && input.files && input.files.length) state[key] = { file: true };
        }
      }
      return state;
    }

    conditionMet(vis, state) {
      if (!vis || !vis.conditions || vis.conditions.length === 0) return true;
      const results = vis.conditions.map((c) => {
        const sel = state[c.option];
        if (c.is_not !== undefined) {
          if (Array.isArray(sel)) return !sel.includes(c.is_not);
          return sel !== c.is_not;
        }
        if (Array.isArray(sel)) return sel.includes(c.is);
        return sel === c.is;
      });
      return vis.mode === 'any' ? results.some(Boolean) : results.every(Boolean);
    }

    /* effect "show" (default): unmet = hidden. effect "enable": always
     * rendered; unmet = dimmed + inputs locked + required suspended. In both
     * cases an inactive option contributes nothing to the order or price. */
    applyVisibility(el, state) {
      let vis = null;
      try {
        if (el.dataset.visibility) vis = JSON.parse(el.dataset.visibility);
      } catch { /* malformed condition: treat as always visible */ }
      // Supersession (size & fit M2M mode): a superseded option is
      // unconditionally unmet — same pathway as conditions, one owner.
      const met = el.dataset.superseded === '1' ? false : this.conditionMet(vis, state);
      const enableMode = vis && vis.effect === 'enable';
      el.hidden = enableMode ? false : !met;
      el.classList.toggle('opt-locked', enableMode && !met);
      const active = met;
      for (const input of el.querySelectorAll('input, select')) {
        if (input.hasAttribute('data-checkbox-mirror')) continue; // managed below
        if (input.hasAttribute('data-oos')) { input.disabled = true; continue; } // sold-out stage items stay locked
        // Dead choices are not this loop's to revive — always-dead ones never,
        // condition-dead ones only when applyChoiceConditions says so.
        if (input.hasAttribute('data-dead') || input.hasAttribute('data-dead-now')) { input.disabled = true; continue; }
        input.disabled = !active;
        if (input.hasAttribute('required') || el.dataset.required === 'true') {
          input.required = active && el.dataset.required === 'true' && input.type !== 'checkbox';
        }
      }
      return active;
    }

    /* Put one choice into the dead state at runtime, or take it out — the
     * same state the Liquid renders for `disabled: true`. Two callers: the
     * ?opt_dead preview override (permanent, never revived) and the
     * choice-level conditions below (toggled every refresh).
     * el is either an <option> in a native select or a choice input.
     * `permanent` marks the preview's kind, which revive must not undo. */
    setChoiceDead(el, why, permanent) {
      el.disabled = true;
      el.setAttribute(permanent ? 'data-dead' : 'data-dead-now', '');
      if (el.tagName === 'OPTION') {
        if (el.selected) el.closest('select').value = '';
        if (why && !el.dataset.deadText) {
          el.dataset.liveText = el.textContent.trim();
          el.dataset.deadText = `${el.dataset.liveText} — ${why}`;
        }
        if (el.dataset.deadText) el.textContent = el.dataset.deadText;
        return;
      }
      el.checked = false;
      const label = el.id && this.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (!label) return;
      label.classList.add('opt-dead');
      // The Liquid path drops the price suffix on a dead choice, so runtime
      // has to as well, or it shows a card real data never produces. The
      // node is kept (not removed) so a revived choice gets its price back.
      for (const p of label.querySelectorAll('.opt-price')) p.hidden = true;
      // A chip has no text of its own, so its reason rides the hover name.
      if (label.classList.contains('opt-swgrid__chip')) {
        if (why && !label.dataset.deadName) {
          label.dataset.liveName = label.dataset.name || '';
          label.dataset.deadName = `${label.dataset.liveName} — ${why}`.trim();
        }
        if (label.dataset.deadName) label.dataset.name = label.dataset.deadName;
        return;
      }
      const text = label.querySelector('.opt-check__label, .opt-combo__label, .opt-label__text');
      if (why && text && !text.querySelector('.opt-dead__why')) {
        const line = document.createElement('span');
        line.className = 'opt-dead__why';
        line.textContent = why;
        text.appendChild(line);
      }
    }

    /* The inverse. Never revives a choice the Liquid or the preview killed
     * outright — `data-dead` is not this method's to undo. */
    setChoiceLive(el) {
      if (el.hasAttribute('data-dead')) return;
      el.removeAttribute('data-dead-now');
      el.disabled = false;
      if (el.tagName === 'OPTION') {
        if (el.dataset.liveText) el.textContent = el.dataset.liveText;
        return;
      }
      const label = el.id && this.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (!label) return;
      label.classList.remove('opt-dead');
      for (const p of label.querySelectorAll('.opt-price')) p.hidden = false;
      if (label.dataset.liveName !== undefined) label.dataset.name = label.dataset.liveName;
      label.querySelector('.opt-dead__why')?.remove();
    }

    /* Every choice label on the page, by option key then choice key. Built
     * once from the DOM rather than from the display doc, so shared-unit
     * refs are already expanded and inline options need no special case. */
    buildChoiceLabels() {
      this.choiceLabels = {};
      for (const w of this.wrappers) {
        const key = w.dataset.optKey;
        if (!key) continue;
        const map = (this.choiceLabels[key] = {});
        for (const el of w.querySelectorAll('[data-key]')) {
          if (!el.dataset.key) continue;
          // `value` is the bare authored label on every control including
          // <option>, whose text carries the price suffix as well.
          const label = el.value || (el.tagName === 'OPTION' ? el.textContent.trim() : '');
          if (label) map[el.dataset.key] = label;
        }
      }
    }

    /* The reason a conditional choice is dead, in the customer's words.
     * Generated, not authored: the labels are already on the page, so a
     * condition needs no companion string (GUI + theme agreed 2026-08-12).
     * Positives and negatives are phrased separately — "Only without X"
     * is a sentence nobody writes. */
    conditionReason(vis) {
      const label = (c) => (this.choiceLabels[c.option] || {})[c.is !== undefined ? c.is : c.is_not];
      const join = (arr) => (arr.length < 2
        ? arr[0]
        : `${arr.slice(0, -1).join(', ')} ${vis.mode === 'any' ? 'or' : 'and'} ${arr[arr.length - 1]}`);
      const yes = [];
      const no = [];
      for (const c of vis.conditions || []) {
        const l = label(c);
        if (!l) continue; // dangling reference: say nothing rather than guess
        (c.is !== undefined ? yes : no).push(l);
      }
      const parts = [];
      if (yes.length) parts.push(`Only with ${join(yes)}`);
      if (no.length) parts.push(`Not available with ${join(no)}`);
      return parts.join('. ');
    }

    /* Choice-level conditions (contract v1.30). ENABLE-ONLY by user ruling:
     * the row never hides, it dims with its reason. Hiding is legible at
     * option scale — a section appearing after a tick reads as cause and
     * effect — but at choice scale one missing chip reads as a bug, and the
     * customer never learns the capability exists.
     * A selection whose condition goes unmet RESETS to nothing: it cannot
     * stand (the order is unbuildable) and cannot auto-switch (the system
     * never picks for the customer, same law as defaults). So the later
     * pick always wins and the earlier one re-asks.
     * Returns the option keys that lost a pick this pass. */
    applyChoiceConditions(state) {
      const cleared = [];
      for (const el of this.choiceConditions) {
        let vis = null;
        try { vis = JSON.parse(el.dataset.choiceWhen); } catch { continue; }
        if (this.conditionMet(vis, state)) { this.setChoiceLive(el); continue; }
        const wasPicked = el.tagName === 'OPTION' ? el.selected : el.checked;
        const why = el.dataset.choiceWhy || this.conditionReason(vis);
        this.setChoiceDead(el, why, false);
        if (wasPicked) {
          const w = el.closest('[data-opt]');
          if (w && w.dataset.optKey) cleared.push(w.dataset.optKey);
        }
      }
      return cleared;
    }

    /* Builder pages (display doc scaffold:true → .product--builder):
     * preview slots on image-bearing stages, the pack recap, and
     * answered-state marks. Deciding is compact; seeing lives in the
     * per-stage preview pane (hover previews, selection persists). */
    initBuilder() {
      this.isBuilder = !!document.querySelector('.product--builder');
      if (!this.isBuilder) return;
      this.recapWrap = this.querySelector('[data-recap]');
      this.recapRows = this.querySelector('[data-recap-rows]');
      this.recapTotal = this.querySelector('[data-recap-total]');
      for (const lane of this.querySelectorAll('.opt-stage:not(.opt-stage--toggle)')) {
        if (!lane.querySelector('.opt-stagecard__media img')) continue;
        const fig = document.createElement('figure');
        fig.className = 'opt-stage__preview';
        const img = document.createElement('img');
        img.alt = '';
        fig.appendChild(img);
        lane.appendChild(fig);
        lane.classList.add('opt-stage--haspreview');
        const srcFor = (card) => {
          const m = card && card.querySelector('.opt-stagecard__media img');
          const s = m && (m.currentSrc || m.src);
          return s ? s.replace(/width=\d+/g, 'width=600') : null;
        };
        const showSelected = () => {
          const on = lane.querySelector('input[type="checkbox"]:checked');
          const card = (on && lane.querySelector(`label[for="${CSS.escape(on.id)}"]`))
            || lane.querySelector('.opt-stagecard:not([hidden]):not(.opt-stagecard--oos)');
          const s = srcFor(card);
          if (s) img.src = s;
        };
        lane.addEventListener('mouseover', (e) => {
          const card = e.target.closest && e.target.closest('.opt-stagecard');
          if (!card || card.hidden) return;
          const s = srcFor(card);
          if (s) img.src = s;
        });
        lane.addEventListener('mouseleave', showSelected);
        this.addEventListener('change', () => requestAnimationFrame(showSelected));
        showSelected();
      }
    }

    /* True if the wrapper currently holds any customer selection. */
    hasSelection(w) {
      const t = w.dataset.optType;
      if (t === 'radio') return !!w.querySelector('input[type="radio"]:checked');
      if (t === 'upsell' || t === 'checkbox') return !!w.querySelector('input[type="checkbox"]:checked');
      if (t === 'drop_down') {
        const c = w.querySelector('select');
        const chosen = c && c.selectedOptions[0];
        return !!(chosen && chosen.dataset.key);
      }
      return false;
    }

    /* Fill the pack recap from the live selection. Prices come from the
     * same merged pricing view the checkout charges from. */
    updateRecap(adjCents) {
      if (!this.recapRows) return;
      const rows = [];
      for (const w of this.wrappers) {
        if (w.hidden || w.classList.contains('opt-locked')) continue;
        const key = w.dataset.optKey;
        const title = (w.querySelector('legend, .label')?.textContent || key || '')
          .replace(/\*\s*$/, '').replace(/\s*✓\s*$/, '').trim();
        const type = w.dataset.optType;
        if (type === 'upsell') {
          for (const on of w.querySelectorAll('input[type="checkbox"]:checked')) {
            const entry = (this.pricing[key] || {})[on.dataset.key];
            const p = entry && entry.companion ? Number(entry.companion.price) : null;
            rows.push({ t: title, v: on.value, p });
          }
        } else if (type === 'radio') {
          const on = w.querySelector('input[type="radio"]:checked');
          if (on) rows.push({ t: title, v: on.value, p: null });
        } else if (type === 'drop_down') {
          const sel = w.querySelector('select');
          const chosen = sel && sel.selectedOptions[0];
          if (chosen && chosen.dataset.key) rows.push({ t: title, v: chosen.value, p: null });
        }
      }
      this.recapRows.textContent = '';
      for (const r of rows) {
        const row = document.createElement('div');
        row.className = 'opt-recap__row';
        const t = document.createElement('span'); t.textContent = r.t;
        const v = document.createElement('span'); v.textContent = r.v;
        const p = document.createElement('span');
        p.textContent = r.p ? this.formatMoney(Math.round(r.p * 100)) : '';
        row.append(t, v, p);
        this.recapRows.appendChild(row);
      }
      this.recapWrap.hidden = rows.length === 0;
      if (this.recapTotal) this.recapTotal.textContent = this.formatMoney(this.basePrice + adjCents);
    }

    /* Filter steers (offers v1.3): per-item visibility inside builder
     * lanes. Runs after wrapper-level visibility each pass (that pass
     * re-enables every input in an active lane; this one re-disables the
     * filtered-out ones). In pick:one lanes a checked item that gets
     * filtered out hands its check to a newly visible item with the SAME
     * label (a size toggle keeps your size across a thickness change);
     * no same-label survivor = the pick clears. */
    applyItemFilters(state) {
      for (const lane of this.querySelectorAll('[data-opt-type="upsell"]')) {
        const inputs = lane.querySelectorAll('input[data-item-visibility]');
        if (!inputs.length) continue;
        let carryLabel = null;
        for (const input of inputs) {
          let vis = null;
          try { vis = JSON.parse(input.dataset.itemVisibility); } catch { continue; }
          const met = this.conditionMet(vis, state);
          const card = input.nextElementSibling;
          if (!met && input.checked) { carryLabel = input.value; input.checked = false; }
          if (card && card.tagName === 'LABEL') card.hidden = !met;
          input.disabled = !met || input.hasAttribute('data-oos') || input.disabled;
          if (met && !input.hasAttribute('data-oos') && !lane.hidden && !lane.classList.contains('opt-locked')) {
            input.disabled = false;
          }
        }
        if (carryLabel && lane.dataset.pick === 'one') {
          for (const input of inputs) {
            if (!input.disabled && !input.checked && input.value === carryLabel) {
              const card = input.nextElementSibling;
              if (!card || !card.hidden) { input.checked = true; break; }
            }
          }
        }
      }
    }

    /* When a condition clears a pick, say so TWICE — the customer is looking
     * at the option they just changed, not at the one two rows up that
     * silently lost its answer.
     *   - under the option they touched: what it just cost them
     *   - on the option that lost its pick: the same unanswered state it
     *     would have shown at Add to Cart, brought forward to now
     * Both persist until the option is answered again. Deliberately no
     * pulse or flash: a timed highlight is invisible to anyone scrolled
     * past the cleared option, which is the exact case this exists for. */
    showRepickNotices() {
      for (const [key, touchedKey] of this.pendingRepick) {
        const w = this.wrappers.find((x) => x.dataset.optKey === key);
        // Answered again, or gone entirely — the notice has done its job.
        if (!w || w.hidden || this.hasSelection(w)) {
          if (w) this.clearError(w);
          this.clearConsequence(touchedKey);
          this.pendingRepick.delete(key);
          continue;
        }
        const title = this.optionTitle(w);
        // Only a REQUIRED option re-enters validation — an optional one that
        // lost its pick is not a problem to solve, just a fact to report, and
        // the live-clear pass at the end of refresh would strip the message
        // anyway (validateOne passes anything not required).
        if (w.dataset.required === 'true') this.showError(w, `Please choose ${title} again`);
        const touched = this.wrappers.find((x) => x.dataset.optKey === touchedKey);
        if (touched && touched !== w) {
          let note = touched.querySelector('.opt-consequence');
          if (!note) {
            note = document.createElement('p');
            note.className = 'opt-consequence';
            touched.appendChild(note);
          }
          note.textContent = `This clears your ${title} choice.`;
        }
      }
    }

    clearConsequence(key) {
      const touched = key && this.wrappers.find((x) => x.dataset.optKey === key);
      touched?.querySelector('.opt-consequence')?.remove();
    }

    /* The option's customer-facing name, as validation words it.
     * .opt-label__title before .label/legend: the label row also holds the
     * (i) button, whose text is a bare "i", so reading the whole row named
     * an option "Body Length *i" — visible in validation messages on every
     * info-carrying option since v1.24. */
    optionTitle(w) {
      const el = w.querySelector('.opt-label__title') || w.querySelector('.label, legend');
      return (el?.textContent || w.dataset.optTitle || 'this option')
        .replace(/\*\s*$/, '').trim();
    }

    /* Adjustment total in cents for the current state. Mirrors the checkout
     * function's math exactly (fixed + percent-of-base + length tables). */
    adjustmentCents(state) {
      let fixed = 0;
      let percent = 0;
      for (const [optKey, sel] of Object.entries(state)) {
        const table = this.pricing[optKey];
        if (!table) continue;
        if (sel && typeof sel === 'object' && !Array.isArray(sel)) {
          const fee = table._field_fee;
          if (fee) { fixed += Number(fee.fixed) || 0; percent += Number(fee.percent) || 0; }
          if (table._length && typeof sel.len === 'number') {
            fixed += this.lengthAmount(table._length, sel.len);
          }
          continue;
        }
        const keys = Array.isArray(sel) ? sel : [sel];
        for (const k of keys) {
          const adj = table[k];
          if (!adj) continue;
          if (adj.companion) { fixed += Number(adj.companion.price) || 0; continue; }
          fixed += Number(adj.fixed) || 0;
          percent += Number(adj.percent) || 0;
        }
      }
      return Math.round(fixed * 100 + (this.basePrice * percent) / 100);
    }

    lengthAmount(table, len) {
      const lengths = Object.keys(table).map(Number).filter((n) => Number.isInteger(n) && n > 0).sort((a, b) => a - b);
      if (!lengths.length) return 0;
      let chosen = lengths[0];
      for (const n of lengths) { if (n <= len) chosen = n; else break; }
      if (len >= lengths[lengths.length - 1]) chosen = lengths[lengths.length - 1];
      return Number(table[String(chosen)]) || 0;
    }

    formatMoney(cents) {
      const symbol = (window.theme && theme.settings && theme.settings.moneyFormat) ? null : '£';
      const amount = (cents / 100).toFixed(2);
      return symbol ? symbol + amount : amount;
    }

    refresh() {
      // Size & fit M2M supersession flags first — refresh() is the one
      // gate every state source passes through (clicks, URL restore,
      // defaults), so mode state can never go stale against the URL.
      for (const f of this.sizeFitSyncs || []) f();
      // Visibility can depend on options that other rules hide (e.g. a
      // pre-checked default inside a gated block) — iterate until the state
      // stops changing (bounded; chains are shallow).
      let state = this.getState();
      for (let pass = 0; pass < 4; pass++) {
        for (const g of this.groups) this.applyVisibility(g, state);
        for (const w of this.wrappers) {
          const parentGroup = w.closest('[data-opt-group]');
          if (parentGroup && parentGroup.hidden) {
            w.hidden = true;
            for (const input of w.querySelectorAll('input, select')) input.disabled = true;
            continue;
          }
          this.applyVisibility(w, state);
          if (parentGroup && parentGroup.classList.contains('opt-locked')) {
            w.classList.add('opt-locked');
            for (const input of w.querySelectorAll('input, select')) input.disabled = true;
          }
        }
        this.applyItemFilters(state);
        for (const key of this.applyChoiceConditions(state)) {
          this.pendingRepick.set(key, this.lastTouchedKey);
        }
        const next = this.getState();
        const settled = JSON.stringify(next) === JSON.stringify(state);
        state = next;
        if (settled) break;
      }
      this.showRepickNotices();

      // Gate pills (data-gate-hide) disappear visually once ticked — the
      // block's header Remove link takes over. CSS class, NOT hidden: the
      // state must stay live for conditions and _optsel.
      for (const w of this.wrappers) {
        if (!w.hasAttribute('data-gate-hide')) continue;
        w.classList.toggle('opt-gate-done', !!w.querySelector('input[type="checkbox"]:checked'));
      }

      // Checkbox/upsell mirrors: joined labels, enabled only when ticked.
      for (const w of this.wrappers) {
        if (w.dataset.optType !== 'checkbox' && w.dataset.optType !== 'upsell') continue;
        const mirror = w.querySelector('[data-checkbox-mirror]');
        if (mirror) {
          const on = Array.from(w.querySelectorAll('input[type="checkbox"]:checked'));
          mirror.value = on.map((i) => i.value).join(', ');
          mirror.disabled = on.length === 0 || w.hidden;
        }
        // Enforce max_selectable on upsell blocks: at the cap, unticked
        // items lock (and dim) until something is unticked.
        if (w.dataset.optType === 'upsell' && !w.hidden) {
          const max = parseInt(w.dataset.max, 10) || 99;
          const boxes = Array.from(w.querySelectorAll('input[type="checkbox"]'));
          const checkedCount = boxes.filter((b) => b.checked).length;
          for (const b of boxes) {
            const lock = !b.checked && checkedCount >= max;
            b.disabled = lock;
            b.nextElementSibling?.classList.toggle('opt-upsell__card--capped', lock);
          }
        }
      }

      // Machine-readable selections for the pricing function.
      const optsel = {};
      for (const [k, v] of Object.entries(state)) {
        if (Array.isArray(v)) optsel[k] = v.join(',');
        else if (typeof v === 'object') {
          if (this.pricing[k] && (this.pricing[k]._length || this.pricing[k]._field_fee)) optsel[k] = v;
        } else optsel[k] = v;
      }
      this.optselInput.value = JSON.stringify(optsel);

      // Before the _mirror collection: the sensitivity note maintains its own
      // mirrored property, which must be current within this same pass.
      this.updateSensitivityNote();

      // Swatch grids: label row mirrors the selection (name + chip) live.
      // Openness is EXPLICIT and deterministic: seeded ONCE at first
      // refresh (the first active unpicked grid opens — three open grids
      // is a wall), then grids move only on user action — picking pins
      // the grid you're in, engaging elsewhere folds it, the row toggles
      // it. Nothing ever opens or closes by itself after load (a live
      // first-unpicked rule made the open slot migrate on every pick —
      // felt haunted). Collapsed-with-pick reads as a committed entry
      // (opt-swgrid--set strengthens the row value).
      if (!this.swgridSeeded) {
        this.swgridSeeded = true;
        const first = Array.from(this.querySelectorAll('.opt-swgrid')).find((g) =>
          !g.hidden && !g.classList.contains('opt-locked') &&
          !g.querySelector('.opt-swgrid__panel input:checked'));
        if (first) first.dataset.open = '1';
      }
      for (const grid of this.querySelectorAll('.opt-swgrid')) {
        const panel = grid.querySelector('[data-swgrid-panel]');
        const nameEl = grid.querySelector('[data-swgrid-name]');
        const chipEl = grid.querySelector('[data-swgrid-chip]');
        const toggle = grid.querySelector('[data-swgrid-toggle]');
        if (!panel || !nameEl) continue;
        const on = grid.querySelector('.opt-swgrid__panel input:checked');
        if (on) {
          nameEl.textContent = on.value + (on.dataset.priceSuffix ? ' ' + on.dataset.priceSuffix : '');
          const src = grid.querySelector(`label[for="${on.id}"] .opt-swatch`);
          if (src && chipEl) {
            chipEl.style.background = src.style.background;
            chipEl.classList.remove('opt-swatch--ghost');
            chipEl.hidden = false;
          }
        } else {
          nameEl.textContent = nameEl.dataset.prompt || '';
          // Ghost chip: the empty state shows the slot the swatch will
          // fill — a dashed outline reads as "input awaiting a pick"
          // where bare placeholder text read as a status label.
          if (chipEl) {
            chipEl.style.background = 'transparent';
            chipEl.classList.add('opt-swatch--ghost');
            chipEl.hidden = false;
          }
        }
        const open = grid.dataset.open === '1';
        panel.classList.toggle('is-open', open);
        // before opt-ready, state flips are instant (no transitionend) —
        // keep the settled clip-release in step by hand
        if (!this.classList.contains('opt-ready')) {
          panel.classList.toggle('opt-swgrid__panel--settled', open);
        }
        grid.classList.toggle('opt-swgrid--set', !open && !!on);
        if (toggle) toggle.setAttribute('aria-expanded', String(open));
      }
      this.syncRepeatedPickers();
      this.syncYourColours();

      // Custom Colourway palette: every ACTIVE picked choice with a swatch
      // chip (decal colours included), deduped by colour, DOM order. Purely
      // derived — nothing is authored and nothing reaches the order.
      const palette = this.querySelector('[data-palette]');
      if (palette) {
        const colours = [];
        for (const w of this.wrappers) {
          if (w.hidden || w.classList.contains('opt-locked')) continue;
          for (const on of w.querySelectorAll('input:checked')) {
            const row = w.querySelector(`label[for="${on.id}"]`);
            const chip = row && row.querySelector('.opt-combo__chip, .opt-swatch');
            const c = chip && chip.style.background;
            // The card wears the finish of the palette the colour came from,
            // so a matte grid can't sit above glossy cards. Sources without a
            // finish (combos, decal colours) default to gloss.
            const f = w.dataset.finish || 'gloss';
            if (c && !colours.some((it) => it.c === c)) {
              colours.push({ c, name: on.value || '', f, key: on.dataset.key || '', material: f === 'matte' ? 'leather' : 'rubber' });
            }
          }
        }
        const key = colours.map((it) => it.c + it.name + it.f).join('|');
        if (key !== this.paletteKey) {
          this.paletteKey = key;
          // A fanned hand of swatch cards (user-designed 2026-08-05):
          // rotations symmetric around the midpoint of the count, ascending
          // z so later picks sit on top, overlap via negative margins.
          // Hover lifts a card out of the stack and names it (CSS).
          const tiles = palette.querySelector('[data-palette-tiles]');
          tiles.innerHTML = '';
          const n = colours.length;
          const step = n > 1 ? Math.min(9, 30 / (n - 1)) : 0;
          colours.forEach((it, i) => {
            // wrapper = stationary hover target (geometry only); the face
            // child does the hover lift — a lifting hover target un-hovers
            // itself at the cursor's edge and flickers (user-caught)
            const card = document.createElement('span');
            card.className = 'opt-palette__card';
            const face = document.createElement('span');
            face.className = 'opt-palette__card-face';
            face.style.background = it.c;
            face.dataset.name = it.name;
            face.dataset.finish = it.f;
            card.appendChild(face);
            const rot = (i - (n - 1) / 2) * step;
            card.style.setProperty('--rot', `${rot.toFixed(2)}deg`);
            // real-fan arc: outer cards drop as they rotate away, middle
            // crowns. sin (near-linear), not 1-cos (quadratic) — the
            // quadratic read fine at 3 cards but plunged the outer pair
            // at 4+ (user-caught).
            const drop = Math.sin(Math.abs(rot * Math.PI) / 180) * 30;
            card.style.setProperty('--drop', `${drop.toFixed(1)}px`);
            card.style.zIndex = String(i + 1);
            tiles.appendChild(card);
          });
          const title = palette.querySelector('.opt-palette__title');
          const cw = this.colourwayTitle(colours);
          if (title) title.textContent = cw.title;
          // The look name rides the order as a visible "Personal colourway"
          // property when there is a full match: usage for the shop, the
          // customer's own name for it in their order history, and a word
          // that stops the workshop reading it as a colour spec.
          const cwInput = this.querySelector('[data-colourway]');
          if (cwInput) { cwInput.value = cw.look || ''; cwInput.disabled = !cw.look; }
        }
        // Heading and tiles wait for a pick individually; the wrapper stays
        // whenever it holds the swatch-book link, which is a pre-purchase
        // aid and has to be readable before anything is chosen — so the
        // wrapper only disappears entirely on products with no link.
        const empty = colours.length === 0;
        const titleEl = palette.querySelector('[data-palette-title]');
        const tilesEl = palette.querySelector('[data-palette-tiles]');
        const book = palette.querySelector('.opt-swatch-book');
        if (titleEl) titleEl.hidden = empty;
        if (tilesEl) tilesEl.hidden = empty;
        palette.hidden = empty && !book;
      }

      // Band hint: the selected size band's empirical max-size, shown on
      // the right of the placement heading (data-size-hint names the size
      // option it mirrors). Empty until a band is picked.
      for (const hintEl of this.querySelectorAll('[data-size-hint]')) {
        const w = this.querySelector(`[data-opt-key="${hintEl.dataset.sizeHint}"]`);
        if (!w) continue;
        const on = w.querySelector('input:checked') || w.querySelector('select')?.selectedOptions[0];
        hintEl.textContent = (on && on.dataset.hint) || '';
      }

      // Live pictogram tinting: garment takes the body colour, decal zone
      // takes that instance's decal colour (rough by design — swatch hexes,
      // not renders). Resting state stays black/white until picked.
      const chipColour = (w) => {
        if (!w || w.hidden) return null;
        const on = w.querySelector('input:checked');
        if (!on) return null;
        const row = w.querySelector(`label[for="${on.id}"]`);
        const chip = row && row.querySelector('.opt-combo__chip, .opt-swatch');
        return (chip && chip.style.background) || null;
      };
      for (const cards of this.querySelectorAll('.opt-size-cards')) {
        const m = (cards.dataset.optKey || '').match(/^decal_(\d+)_size$/);
        // tint: false (v1.26): the FULL neutral resting state (black body,
        // white patch). Keeping the patch tint alone was considered and
        // rejected (user): a dark pick barely registers on the black
        // resting body and black is invisible — half-tinted is worse
        // than untinted.
        const off = cards.hasAttribute('data-tint-off');
        const g = off ? null : chipColour(this.tintAnchor);
        const d = !off && m ? chipColour(this.querySelector(`[data-opt-key="decal_${m[1]}_colour"]`)) : null;
        if (g) cards.style.setProperty('--opt-garment', g); else cards.style.removeProperty('--opt-garment');
        if (d) cards.style.setProperty('--opt-decal', d); else cards.style.removeProperty('--opt-decal');
      }

      // Zoned colourway preview (v1.27): fill each tagged zone from its
      // bound picker's chip. Unlike the pictogram tint, a HIDDEN picker's
      // zones hold their default/last-known value (user-ratified:
      // held-inert beats blanking — the radios-mode cards keep showing
      // every style's colours while its picker is off-screen). Shape
      // groups: clones are locked to one shape; the hero follows the
      // pick (none picked = the file's first group).
      const zoneColour = (w) => {
        if (!w) return null;
        const on = w.querySelector('input:checked');
        if (!on) return null;
        const row = w.querySelector(`label[for="${on.id}"]`);
        const chip = row && row.querySelector('.opt-combo__chip, .opt-swatch');
        return (chip && chip.style.background) || null;
      };
      for (const zp of this.querySelectorAll('[data-zoned-preview] svg, svg[data-zoned-clone]')) {
        for (const z of zp.querySelectorAll('.opt-svg-zone[data-zone-key]')) {
          const w = this.querySelector(`[data-opt-key="${z.dataset.zoneKey}"]`);
          const c = zoneColour(w);
          // no pick = the art's own resting fill (black/white default);
          // hidden pickers still HOLD because their checked state persists
          if (c) z.style.fill = c; else z.style.removeProperty('fill');
        }
        const locked = zp.getAttribute('data-locked-shape');
        const holder = zp.closest('[data-zoned-preview]');
        const shapeKey = locked === null && holder ? holder.dataset.shapeOption : null;
        const shapeW = shapeKey ? this.querySelector(`[data-opt-key="${shapeKey}"]`) : null;
        const picked = shapeW && shapeW.querySelector('input:checked');
        const want = locked !== null ? locked : (picked ? picked.dataset.key : null);
        const groups = [...zp.querySelectorAll('.opt-svg-shape')];
        const match = want && groups.find((g) => g.dataset.shape === want);
        const show = match || groups[0];
        for (const g of groups) g.style.display = g === show ? '' : 'none';
      }

      // Consolidated human-readable properties (label → value) so the
      // pricing function can re-stamp them onto the bundle's main component
      // after a companion lineExpand (which strands the originals on the
      // bundle parent). File inputs excluded — their property value (the CDN
      // URL) only exists after cart add; the function passes those through
      // by their static "Decal n artwork" names instead.
      if (this.mirrorInput) {
        const mirror = {};
        for (const el of this.querySelectorAll('input[name^="properties["], select[name^="properties["]')) {
          if (el.disabled || el.type === 'file') continue;
          if (el.type === 'hidden' && !el.hasAttribute('data-checkbox-mirror')) continue;
          if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) continue;
          const name = el.name.slice(11, -1); // properties[X] → X
          if (!name || name.startsWith('_')) continue;
          const value = el.value.trim();
          if (value) mirror[name] = value;
        }
        const has = Object.keys(mirror).length > 0;
        this.mirrorInput.value = JSON.stringify(mirror);
        this.mirrorInput.disabled = !has;
      }

      // Live-clear inline errors as they're resolved (never add new ones here
      // — fresh errors only appear on submit attempts).
      for (const w of this.wrappers) {
        if (w.classList.contains('opt-invalid')) {
          const still = this.validateOne(w);
          if (!still) this.clearError(w);
        }
      }

      this.updateComboTriggers();
      const cfg = this.serializeConfig();
      this.updateUrl(cfg);
      if (this.resetBtn) {
        const pristine = !Object.keys(cfg.s).length && !Object.keys(cfg.t).length && !cfg.f.length;
        if (this.resetConfirmBtn) {
          this.resetBtn.classList.toggle('is-idle', pristine);
          if (pristine) this.disarmReset();
        } else {
          this.resetBtn.hidden = pristine;
        }
      }

      // Running total + live main price.
      const adj = this.adjustmentCents(state);
      if (this.totalRow) {
        // Held on the pinned layout (see heldTotal above); elsewhere it is
        // still a row that appears only once something is priced.
        this.totalRow.hidden = this.heldTotal ? false : adj === 0;
        if (adj !== 0 || this.heldTotal) this.totalValue.textContent = this.formatMoney(this.basePrice + adj);
      }
      if (this.configuredValue) {
        const configured = adj !== 0;
        this.priceValueEl.style.display = configured ? 'none' : '';
        this.configuredValue.hidden = !configured;
        if (configured) this.configuredValue.textContent = this.formatMoney(this.basePrice + adj);
        this.configuredMark.hidden = !configured;
      }
      if (this.isBuilder) {
        this.updateRecap(adj);
        for (const w of this.wrappers) {
          w.classList.toggle('is-answered', !w.hidden && !w.classList.contains('opt-locked') && this.hasSelection(w));
        }
      }
      if (this.stickyAtcPrice === null) {
        this.stickyAtcPrice = document.querySelector('sticky-atc-panel .price__current') || false;
        if (this.stickyAtcPrice) this.stickyAtcOriginal = this.stickyAtcPrice.innerHTML;
      }
      if (this.stickyAtcPrice) {
        if (adj !== 0) {
          this.stickyAtcPrice.textContent = this.formatMoney(this.basePrice + adj);
        } else {
          this.stickyAtcPrice.innerHTML = this.stickyAtcOriginal;
        }
      }
    }
  }

  customElements.define('product-options', ProductOptions);
})();
